import { SUBSCRIBABLE_OUTBOUND_EVENT_TYPES } from '@developerz/domain';
import { IconShield } from '@developerz/ui';
import { createSignal, For, type JSX } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Add-a-webhook modal (#807, mock AddActionModal kind=webhook): signed-
// deliveries alert, name, endpoint URL, subscribed-event set. Presenter — the
// route owns the POST and shows the one-time signing secret after create.

/**
 * The events on offer are the SUBSCRIBABLE set — the ones a producer actually
 * fans out — not `OutboundEventType`, which also declares ten we never emit.
 * This used to offer every member but the synthetic `test.ping`, so nine of the
 * twelve checkboxes bought a subscription that could never fire; `POST
 * /v1/webhooks` now refuses them, and a picker that offers what the API refuses
 * is a form whose only feedback is a 400.
 */
const SUBSCRIBABLE_EVENTS: readonly string[] = SUBSCRIBABLE_OUTBOUND_EVENT_TYPES;

export type AddWebhookModalProps = {
  readonly open: boolean;
  /** True while the POST is in flight — disables the submit. */
  readonly pending: boolean;
  /** Human error from the API (e.g. duplicate name), shown inline. */
  readonly error?: string | null;
  readonly onCreate: (body: { name: string; url: string; events: string[] }) => void;
  readonly onClose: () => void;
};

export function AddWebhookModal(props: AddWebhookModalProps): JSX.Element {
  const [name, setName] = createSignal('');
  const [url, setUrl] = createSignal('');
  const [events, setEvents] = createSignal<ReadonlySet<string>>(new Set());

  const toggle = (event: string): void => {
    setEvents((cur) => {
      const next = new Set(cur);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return next;
    });
  };

  const valid = (): boolean =>
    name().trim().length > 0 && url().trim().length > 0 && events().size > 0;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onCreate({ name: name().trim(), url: url().trim(), events: [...events()] });
  };

  return (
    <Modal
      open={props.open}
      size="lg"
      title={t('account_webhooks.add_title')}
      desc={t('account_webhooks.add_desc')}
      onClose={props.onClose}
    >
      <form class="add-webhook__form" onSubmit={submit}>
        <div class="alert alert--icon" role="alert">
          <IconShield />
          <p class="alert__title">{t('account_webhooks.signed_title')}</p>
          <p class="alert__desc">{t('account_webhooks.signed_desc')}</p>
        </div>
        <div class="field-group">
          <Field label={t('account_webhooks.form_name')} id="add-webhook-name">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder={t('account_webhooks.form_name_placeholder')}
                required
              />
            )}
          </Field>
          <Field label={t('account_webhooks.endpoint_label')} id="add-webhook-endpoint">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="url"
                value={url()}
                onInput={(e) => setUrl(e.currentTarget.value)}
                placeholder={t('account_webhooks.endpoint_placeholder')}
                required
              />
            )}
          </Field>
          <Field
            as="fieldset"
            label={t('account_webhooks.form_events')}
            id="add-webhook-events"
            desc={t('account_webhooks.events_desc')}
          >
            {() => (
              <For each={SUBSCRIBABLE_EVENTS}>
                {(event) => (
                  <label class="add-webhook__event">
                    <input
                      type="checkbox"
                      checked={events().has(event)}
                      onChange={() => toggle(event)}
                    />
                    <code>{event}</code>
                  </label>
                )}
              </For>
            )}
          </Field>
        </div>

        {props.error ? (
          <p class="field__error" role="alert">
            {props.error}
          </p>
        ) : null}

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('account_webhooks.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!valid() || props.pending}>
            {t(props.pending ? 'account_webhooks.form_creating' : 'account_webhooks.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
